import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLocale } from '../contexts/LocaleContext'
import { siteConfig, getAffiliateLink } from '../config/site'
import { updateSEO } from '../utils/seo'
import { loadArticle } from '../utils/loadArticle'
import { markdownToHtml, parseFaqFromMarkdown } from '../utils/markdownToHtml'
import ReviewCard from '../components/ReviewCard'
import './ArticlePage.css'

const SegmentPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { locale, t } = useLocale()
  const slug = location.pathname.split('/').pop()
  const [pageData, setPageData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [openFaqIndex, setOpenFaqIndex] = useState(null)

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true)
      const data = await loadArticle(slug, locale, 'page')
      setPageData(data)
      
      if (data) {
        updateSEO({
          title: data.metaTitle,
          description: data.metaDescription,
          keywords: data.metaKeywords,
          url: `${siteConfig.baseUrl}/${slug}`
        })
      }
      setLoading(false)
    }
    loadPage()
  }, [slug, locale])

  if (loading) {
    return <div className="article-page-container"><div className="loading">{t('pages.loading')}</div></div>
  }

  if (!pageData) {
    navigate('/')
    return null
  }

  return (
    <div className="article-page-container">
      <button className="back-button" onClick={() => navigate('/')}>
        ← {t('pages.backToHome')}
      </button>

      <div className="article-content">
        <h1 className="article-title">{pageData.title}</h1>
        
        {pageData.introduction && (
          <div className="article-section" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.introduction) }} />
        )}

        {pageData.step_by_step_guide && pageData.step_by_step_guide.steps && Array.isArray(pageData.step_by_step_guide.steps) && (
          <div className="article-section step-by-step-section">
            {pageData.step_by_step_guide.heading && (
              <h2 className="step-by-step-heading">{pageData.step_by_step_guide.heading}</h2>
            )}
            <ol className="step-by-step-list">
              {pageData.step_by_step_guide.steps.map((step, i) => (
                <li key={i} className="step-by-step-item">
                  <h3 className="step-title">
                    {step.step_number && <span className="step-number">{step.step_number}. </span>}
                    {step.title}
                  </h3>
                  {step.description && (
                    <div className="step-description" dangerouslySetInnerHTML={{ __html: markdownToHtml(step.description) }} />
                  )}
                  {step.sub_points && Array.isArray(step.sub_points) && step.sub_points.length > 0 && (
                    <div className="step-sub-points">
                      {step.sub_points.map((point, j) => (
                        <div key={j} className="step-sub-point" dangerouslySetInnerHTML={{ __html: markdownToHtml(point) }} />
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}
        
        {pageData.reviews && Array.isArray(pageData.reviews) && (
          <div className="article-section">
            <h2>{t('pages.segment.sectionTitles.appReviews')}</h2>
            {pageData.reviews.map((review, i) => (
              <ReviewCard key={i} review={review} />
            ))}
          </div>
        )}
        
        {pageData.instant_options && typeof pageData.instant_options === 'object' && (
          <div className="article-section instant-options-section">
            {pageData.instant_options.heading && (
              <div className="section-heading" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.instant_options.heading) }} />
            )}
            {pageData.instant_options.content && (
              <div className="section-content" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.instant_options.content) }} />
            )}
            {pageData.instant_options.fastest_ways && Array.isArray(pageData.instant_options.fastest_ways) && (
              <ul className="fastest-ways-list">
                {pageData.instant_options.fastest_ways.map((item, i) => (
                  <li key={i} className="fastest-way-item">
                    {item.title && <h3 dangerouslySetInnerHTML={{ __html: markdownToHtml(item.title) }} />}
                    {item.description && <div dangerouslySetInnerHTML={{ __html: markdownToHtml(item.description) }} />}
                  </li>
                ))}
              </ul>
            )}
            {pageData.instant_options.why_lovemur && (
              <div className="why-lovemur">
                {pageData.instant_options.why_lovemur.heading && (
                  <div dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.instant_options.why_lovemur.heading) }} />
                )}
                {pageData.instant_options.why_lovemur.points && Array.isArray(pageData.instant_options.why_lovemur.points) && (
                  <ul className="points-list">
                    {pageData.instant_options.why_lovemur.points.map((point, j) => (
                      <li key={j} dangerouslySetInnerHTML={{ __html: markdownToHtml(point) }} />
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {pageData.best_times && typeof pageData.best_times === 'object' && (
          <div className="article-section best-times-section">
            {pageData.best_times.heading && (
              <div className="section-heading" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.best_times.heading) }} />
            )}
            {pageData.best_times.content && (
              <div className="section-content" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.best_times.content) }} />
            )}
            {pageData.best_times.peak_times && Array.isArray(pageData.best_times.peak_times) && (
              <div className="peak-times">
                <h3>Peak times</h3>
                <ul className="peak-times-list">
                  {pageData.best_times.peak_times.map((item, i) => (
                    <li key={i} className="peak-time-item">
                      {item.time && <strong dangerouslySetInnerHTML={{ __html: markdownToHtml(item.time) }} />}
                      {item.description && <div dangerouslySetInnerHTML={{ __html: markdownToHtml(item.description) }} />}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {pageData.best_times.strategic_locations && Array.isArray(pageData.best_times.strategic_locations) && (
              <div className="strategic-locations">
                <h3>Strategic locations</h3>
                <ul className="strategic-locations-list">
                  {pageData.best_times.strategic_locations.map((item, i) => (
                    <li key={i} className="strategic-location-item">
                      {item.location && <strong dangerouslySetInnerHTML={{ __html: markdownToHtml(item.location) }} />}
                      {item.description && <div dangerouslySetInnerHTML={{ __html: markdownToHtml(item.description) }} />}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {pageData.content && (
          <div className="article-section" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.content) }} />
        )}
        
        {pageData.guide && (
          <div className="article-section" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.guide) }} />
        )}
        
        {pageData.tips && typeof pageData.tips === 'object' && (
          <div className="article-section tips-section">
            {(pageData.tips.title || pageData.tips.heading) && (
              <h2>{pageData.tips.title || pageData.tips.heading}</h2>
            )}
            {pageData.tips.content && (
              <div dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.tips.content) }} />
            )}
            {(pageData.tips.points || pageData.tips.tips_list) && Array.isArray(pageData.tips.points || pageData.tips.tips_list) && (
              <ul className="tips-list">
                {(pageData.tips.points || pageData.tips.tips_list).map((point, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: markdownToHtml(point) }} />
                ))}
              </ul>
            )}
          </div>
        )}
        
        {pageData.tips && typeof pageData.tips === 'string' && (
          <div className="article-section" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.tips) }} />
        )}
        
        {pageData.platforms && typeof pageData.platforms === 'object' && (
          <div className="article-section">
            {pageData.platforms.title && <h2>{pageData.platforms.title}</h2>}
            {pageData.platforms.content && (
              <div dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.platforms.content) }} />
            )}
            {pageData.platforms.platformsList && Array.isArray(pageData.platforms.platformsList) && (
              <ul className="platforms-list">
                {pageData.platforms.platformsList.map((platform, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: markdownToHtml(platform) }} />
                ))}
              </ul>
            )}
          </div>
        )}
        
        {pageData.platforms && typeof pageData.platforms === 'string' && (
          <div className="article-section" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.platforms) }} />
        )}
        
        {pageData.rankings && Array.isArray(pageData.rankings) && (
          <div className="article-section">
            <h2>{t('pages.segment.sectionTitles.topRankings')}</h2>
            {pageData.rankings.map((ranking, i) => (
              <div key={i} className="ranking-item" dangerouslySetInnerHTML={{ __html: markdownToHtml(ranking) }} />
            ))}
          </div>
        )}
        
        {pageData.faq && typeof pageData.faq === 'object' && (
          <div className="article-section faq-section">
            <div className="faq-container">
              <h2 className="faq-title">{(pageData.faq.title || (typeof pageData.faq.heading === 'string' ? pageData.faq.heading.replace(/^#+\s*/, '') : pageData.faq.heading) || t('pages.segment.sectionTitles.faq'))}</h2>
              <div className="faq-list">
                {/* Handle both structures: items and questions */}
                {(pageData.faq.items || pageData.faq.questions || []).map((item, i) => (
                  <div key={i} className={`faq-accordion-item ${openFaqIndex === i ? 'open' : ''}`}>
                    <button 
                      className="faq-accordion-header"
                      onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                      aria-expanded={openFaqIndex === i}
                    >
                      <h3 className="faq-question">{item.question}</h3>
                      <span className="faq-accordion-icon">{openFaqIndex === i ? '−' : '+'}</span>
                    </button>
                    {openFaqIndex === i && (
                      <div className="faq-accordion-content">
                        <div className="faq-answer" dangerouslySetInnerHTML={{ __html: markdownToHtml(item.answer) }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        
        {pageData.faq && typeof pageData.faq === 'string' && (() => {
          const faqs = parseFaqFromMarkdown(pageData.faq)
          if (faqs.length > 0) {
            return (
              <div className="article-section faq-section">
                <div className="faq-container">
                  <h2 className="faq-title">{t('pages.segment.sectionTitles.faq')}</h2>
                  <div className="faq-list">
                    {faqs.map((faq, i) => (
                      <div key={i} className={`faq-accordion-item ${openFaqIndex === i ? 'open' : ''}`}>
                        <button 
                          className="faq-accordion-header"
                          onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                          aria-expanded={openFaqIndex === i}
                        >
                          <h3 className="faq-question">{faq.question}</h3>
                          <span className="faq-accordion-icon">{openFaqIndex === i ? '−' : '+'}</span>
                        </button>
                        {openFaqIndex === i && (
                          <div className="faq-accordion-content">
                            <div className="faq-answer" dangerouslySetInnerHTML={{ __html: markdownToHtml(faq.answer) }} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          }
          // Fallback to plain markdown if parsing fails
          return (
            <div className="article-section faq-section" dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.faq) }} />
          )
        })()}
        
        {pageData.common_mistakes && typeof pageData.common_mistakes === 'object' && (pageData.common_mistakes.heading || pageData.common_mistakes.points) && (
          <div className="article-section common-mistakes-section">
            {pageData.common_mistakes.heading && <h2>{pageData.common_mistakes.heading}</h2>}
            {pageData.common_mistakes.points && Array.isArray(pageData.common_mistakes.points) && (
              <ul className="common-mistakes-list">
                {pageData.common_mistakes.points.map((point, i) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: markdownToHtml(point) }} />
                ))}
              </ul>
            )}
          </div>
        )}

        {pageData.conclusion && (
          <div className="article-section conclusion-section">
            {typeof pageData.conclusion === 'object' && pageData.conclusion !== null ? (
              <>
                {pageData.conclusion.heading && <h2>{pageData.conclusion.heading}</h2>}
                {pageData.conclusion.content && (
                  <div dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.conclusion.content) }} />
                )}
              </>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: markdownToHtml(pageData.conclusion) }} />
            )}
          </div>
        )}
        
        <div className="affiliate-cta">
          <a href={getAffiliateLink()} target="_blank" rel="noopener noreferrer" className="affiliate-button">
            {t('pages.segment.affiliateCTA')}
          </a>
        </div>
      </div>
    </div>
  )
}

export default SegmentPage

